import React from 'react';
import { ArrowUpRight, BadgeCheck, Globe2, Users } from 'lucide-react';
import logo from '../assets/logo.png';
import { allCourses } from '../../data/courses';
import { marketingHref } from '../../lib/surface';

interface AuthSplitProps {
  eyebrow: string;
  title: React.ReactNode;
  lede: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  notes?: React.ReactNode;
}

/**
 * Public account shell. The left half uses the first course image from the
 * marketing homepage so sign-in feels like the public LSEM site, not the LMS
 * console. The right half is the form.
 */
const AuthSplit: React.FC<AuthSplitProps> = ({ eyebrow, title, lede, children, footer, notes }) => {
  const image = allCourses[0]?.image || '';

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="lg:grid lg:min-h-screen lg:grid-cols-2">
        <aside className="relative isolate min-h-[300px] overflow-hidden bg-slate-950 lg:min-h-screen">
          {image && (
            <img
              src={image}
              alt="Students learning export management at LSEM"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950/85 to-blue-900/35" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(59,130,246,0.22),transparent_38%)]" />

          <div className="relative flex min-h-[300px] flex-col px-6 py-7 sm:px-10 sm:py-9 lg:min-h-screen lg:px-12 lg:py-10">
            <a href={marketingHref('/')} className="inline-flex w-fit items-center gap-3 text-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white p-1.5 shadow-lg shadow-slate-950/20">
                <img src={logo} alt="" className="h-full w-full object-contain" />
              </span>
              <span>
                <span className="block text-[15px] font-semibold tracking-[0.16em]">LSEM</span>
                <span className="mt-1 block text-[10px] font-medium uppercase tracking-[0.13em] text-blue-100/75">
                  Lagos School of Export Management
                </span>
              </span>
            </a>

            <div className="mt-auto max-w-[36rem] pt-14 sm:pt-20">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.13em] text-blue-50 backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-300" />
                Export education · Lagos, Nigeria
              </span>
              <h2 className="mt-5 max-w-[13ch] text-[clamp(2rem,4.2vw,3.65rem)] font-bold leading-[1.04] tracking-[-0.04em] text-white">
                The business of export, taught by people who do it.
              </h2>
              <p className="mt-5 max-w-[46ch] text-[14px] leading-6 text-blue-100/85 sm:text-[15px]">
                Practical programmes, live mentorship and a certificate employers can verify — built for the next
                generation of Nigerian exporters.
              </p>

              <div className="mt-8 grid max-w-[30rem] grid-cols-3 gap-4 border-t border-white/15 pt-5">
                <div>
                  <p className="flex items-center gap-1.5 text-[17px] font-semibold text-white">
                    <Users className="h-4 w-4 text-sky-300" strokeWidth={1.7} /> 4,000+
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.11em] text-blue-100/60">Alumni</p>
                </div>
                <div>
                  <p className="flex items-center gap-1.5 text-[17px] font-semibold text-white">
                    <Globe2 className="h-4 w-4 text-sky-300" strokeWidth={1.7} /> 20+
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.11em] text-blue-100/60">Markets</p>
                </div>
                <div>
                  <p className="flex items-center gap-1.5 text-[17px] font-semibold text-white">
                    <BadgeCheck className="h-4 w-4 text-sky-300" strokeWidth={1.7} /> Verified
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.11em] text-blue-100/60">Certificates</p>
                </div>
              </div>
            </div>

            <p className="mt-9 hidden max-w-[38ch] text-[11px] leading-5 text-blue-100/55 lg:block">
              “The mentorship and practical case work changed how I approach buyers and documentation.”
              <span className="mt-1 block text-blue-100/40">LSEM alumnus · Export operations</span>
            </p>
          </div>
        </aside>

        <main className="flex min-h-screen flex-col bg-white">
          <div className="flex items-center justify-between gap-4 px-6 py-5 sm:px-10 lg:px-14">
            <a href={marketingHref('/')} className="flex items-center gap-2.5 lg:hidden">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 p-1.5">
                <img src={logo} alt="" className="h-full w-full object-contain" />
              </span>
              <span className="text-[13px] font-semibold tracking-[0.12em] text-slate-900">LSEM</span>
            </a>
            <a
              href={marketingHref('/')}
              className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 transition-colors hover:text-blue-700"
            >
              Back to lsem.org.ng
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.7} />
            </a>
          </div>

          <div className="flex flex-1 items-center px-6 py-8 sm:px-10 lg:px-14 lg:py-12">
            <div className="mx-auto w-full max-w-[430px]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-600">{eyebrow}</p>
              <h1 className="mt-3 text-[clamp(1.9rem,3vw,2.6rem)] font-bold leading-[1.08] tracking-[-0.035em] text-slate-950">
                {title}
              </h1>
              <p className="mt-3 text-[13.5px] leading-6 text-slate-500">{lede}</p>

              <div className="mt-8">{children}</div>

              {notes && (
                <div className="mt-8 border-t border-slate-200 pt-5 text-[12px] leading-5 text-slate-500">{notes}</div>
              )}

              {footer && <div className="mt-6">{footer}</div>}
            </div>
          </div>

          <div className="px-6 pb-6 text-center text-[10px] leading-5 text-slate-400 sm:px-10 lg:px-14">
            © {new Date().getFullYear()} Lagos School of Export Management · Student accounts are protected by LSEM.
          </div>
        </main>
      </div>
    </div>
  );
};

export default AuthSplit;
